/**
 * GA4 / GTM bootstrap for the SPA — Consent Mode v2 before GTM.
 * Spec: wiki 07-analityka (F1.1). Same container as LP; no direct gtag Measurement ID.
 */

const DEFAULT_GTM_ID = 'GTM-NMJ487VG';

function readViteEnv(name: keyof ImportMetaEnv): string | undefined {
  const value = import.meta.env[name];
  if (value === undefined || value === null) return undefined;
  const trimmed = String(value).trim();
  return trimmed || undefined;
}

function readViteEnvOrDefault(name: keyof ImportMetaEnv, fallback: string): string | undefined {
  const raw = import.meta.env[name];
  if (raw === undefined || raw === null) return fallback;
  const trimmed = String(raw).trim();
  return trimmed || undefined;
}

/**
 * Load GTM / honor consent for marketing tags.
 * Off when VITE_ANALYTICS_ENABLED=false or GTM id cleared; otherwise on (incl. local QA).
 */
export const ANALYTICS_COLLECTION_ENABLED = (() => {
  const override = readViteEnv('VITE_ANALYTICS_ENABLED')?.toLowerCase();
  if (override === 'true' || override === '1') return true;
  if (override === 'false' || override === '0') return false;
  // Empty VITE_GTM_ID= disables collection even with default id unused.
  const rawGtm = import.meta.env.VITE_GTM_ID;
  if (rawGtm !== undefined && String(rawGtm).trim() === '') return false;
  return true;
})();

/** Same public GTM id as LP (wiki `.data/GTM.env`). Empty env clears. */
export const GTM_ID = ANALYTICS_COLLECTION_ENABLED
  ? readViteEnvOrDefault('VITE_GTM_ID', DEFAULT_GTM_ID)
  : undefined;

/** Prefer empty — Google Tag lives in GTM. */
export const GA_MEASUREMENT_ID = ANALYTICS_COLLECTION_ENABLED
  ? readViteEnv('VITE_GA_MEASUREMENT_ID')
  : undefined;

export const CONSENT_STORAGE_KEY = 'mm_cookie_consent_v1';
export const CONSENT_VERSION = 1;
export const CONSENT_OPEN_EVENT = 'mm:open-consent';
/** Fired after GTM script is injected — SPA should send its first `virtual_page_view`. */
export const GTM_READY_EVENT = 'mm:gtm-ready';

export type StoredConsent = {
  v: number;
  analytics: boolean;
  ads: boolean;
  ts: number;
};

export type ConsentGranted = {
  analytics: boolean;
  ads: boolean;
};

export function toConsentGranted(choice: Pick<StoredConsent, 'analytics' | 'ads'>): ConsentGranted {
  return { analytics: choice.analytics, ads: choice.ads };
}

export function readStoredConsent(): StoredConsent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed?.v !== CONSENT_VERSION) return null;
    if (typeof parsed.analytics !== 'boolean' || typeof parsed.ads !== 'boolean') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeStoredConsent(choice: ConsentGranted): void {
  if (typeof window === 'undefined') return;
  const stored: StoredConsent = {
    v: CONSENT_VERSION,
    analytics: choice.analytics,
    ads: choice.ads,
    ts: Date.now(),
  };
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(stored));
}

function pushConsentUpdate(granted: ConsentGranted): void {
  ensureGtagStub();
  window.gtag?.('consent', 'update', {
    analytics_storage: granted.analytics ? 'granted' : 'denied',
    ad_storage: granted.ads ? 'granted' : 'denied',
    ad_user_data: granted.ads ? 'granted' : 'denied',
    ad_personalization: granted.ads ? 'granted' : 'denied',
  });
}

/** Persist Consent Mode + ensure GTM is in the page (do not wait for idle after a choice). */
export function applyConsentUpdate(choice: ConsentGranted): void {
  if (typeof window === 'undefined') return;
  if (!window.__updateAnalyticsConsent) {
    window.__updateAnalyticsConsent = pushConsentUpdate;
  }
  window.__updateAnalyticsConsent(choice);
  if (GTM_ID) {
    injectGtm(GTM_ID);
  }
}

export function openConsentSettings(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));
}

/** Exported for user_id / identity helpers (F1.5). */
export function ensureGtagStubForIdentity(): void {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag === 'function') return;
  // Classic gtag stub — push Arguments object (not a plain array).
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
}

function ensureGtagStub(): void {
  ensureGtagStubForIdentity();
}

/** Consent Mode v2 defaults (denied) + restore stored choice. Call before GTM. */
export function initConsentDefaults(): void {
  if (typeof window === 'undefined') return;
  if (!ANALYTICS_COLLECTION_ENABLED) return;

  ensureGtagStub();
  const gtag = window.gtag;
  if (!gtag) return;

  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });

  window.__updateAnalyticsConsent = pushConsentUpdate;

  const stored = readStoredConsent();
  if (stored) {
    pushConsentUpdate({
      analytics: stored.analytics,
      ads: stored.ads,
    });
  }
}

function injectGtm(gtmId: string): void {
  if (typeof window === 'undefined' || window.__gtmLoaded) return;
  window.__gtmLoaded = true;

  window.dataLayer = window.dataLayer || [];
  // Hint for GTM `C - send_page_view` (app = SPA; LP leaves this unset → true).
  window.dataLayer.push({ mm_send_page_view: false });
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
  // Prefer head — first <script> insertBefore can no-op in some Vite/module setups.
  const mount = document.head || document.documentElement;
  mount.appendChild(script);

  window.dispatchEvent(new CustomEvent(GTM_READY_EVENT));
}

/** Load GTM after idle (same idea as LP). No-op without GTM_ID. */
export function loadGtm(): void {
  if (typeof window === 'undefined' || !GTM_ID) return;

  const gtmId = GTM_ID;
  const run = () => injectGtm(gtmId);

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 2500 });
    return;
  }

  if (document.readyState === 'complete') {
    run();
    return;
  }

  window.addEventListener('load', run, { once: true });
}

/**
 * F1.1 entry: Consent Mode defaults first.
 * GTM loads only after a stored choice or a banner decision (`applyConsentUpdate`) —
 * so Tag Assistant / Google Tag do not run before the user answers the banner.
 */
export function initAnalytics(): void {
  initConsentDefaults();
  // Returning visitor with a saved choice → load GTM (consent update already applied above).
  if (readStoredConsent() && GTM_ID) {
    loadGtm();
  }
}

/** True when Statystyka is on and GTM has been injected (safe to push marketing events). */
export function canPushAnalyticsToDataLayer(): boolean {
  if (typeof window === 'undefined') return false;
  if (!ANALYTICS_COLLECTION_ENABLED) return false;
  if (!window.__gtmLoaded) return false;
  const stored = readStoredConsent();
  return Boolean(stored?.analytics);
}

/**
 * SPA virtual page view for GTM (F1.2).
 * dataLayer event = `virtual_page_view`; GTM tag sends GA4 `page_view`.
 * Do not call gtag('event', 'page_view') while GTM owns the Google Tag.
 *
 * Gated on analytics consent + GTM loaded. Module-level dedupe avoids double push
 * from React Strict Mode / HMR remounts (same path within 2s).
 */
let lastVirtualPageViewKey = '';
let lastVirtualPageViewAt = 0;

export function pushVirtualPageView(path: string, search = '', title?: string): void {
  if (!canPushAnalyticsToDataLayer()) return;

  const page_path = `${path}${search}`;
  const now = Date.now();
  if (page_path === lastVirtualPageViewKey && now - lastVirtualPageViewAt < 2000) {
    return;
  }
  lastVirtualPageViewKey = page_path;
  lastVirtualPageViewAt = now;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'virtual_page_view',
    page_path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
    __updateAnalyticsConsent?: (granted: ConsentGranted) => void;
    __gtmLoaded?: boolean;
  }
}
